import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { getSupabaseClient } from '../utils/supabase';
import {
  preparePasswordRecoverySession,
  updatePasswordAfterRecovery,
  formatPasswordResetError,
} from '../utils/passwordReset';

const LOGIN_GOLD = '#d4af37';

export default function ResetPasswordScreen({ navigation }) {
  const theme = useTheme();
  const [ready, setReady] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const supabase = getSupabaseClient();

    if (!supabase) {
      setCheckingLink(false);
      return undefined;
    }

    const finishChecking = (hasSession) => {
      if (cancelled) return;
      if (hasSession) setReady(true);
      setCheckingLink(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || (session && event !== 'SIGNED_OUT')) {
        finishChecking(true);
      }
      if (event === 'INITIAL_SESSION') {
        finishChecking(Boolean(session));
      }
    });

    (async () => {
      try {
        await preparePasswordRecoverySession();
        const { data: { session } } = await supabase.auth.getSession();
        finishChecking(Boolean(session));
      } catch (error) {
        console.error('Recovery session error:', error);
        finishChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async () => {
    if (password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setBusy(true);
    try {
      await updatePasswordAfterRecovery(password);
      setDone(true);
    } catch (error) {
      console.error('Reset password error:', error);
      alert(formatPasswordResetError(error));
    } finally {
      setBusy(false);
    }
  };

  const styles = getStyles(theme);

  if (checkingLink) {
    return (
      <View style={styles.centered} data-screen="reset-password">
        <StatusBar style="light" />
        <ActivityIndicator size="large" color={LOGIN_GOLD} />
        <Text style={styles.hint}>Checking reset link…</Text>
      </View>
    );
  }

  if (!ready && !done) {
    return (
      <View style={styles.centered} data-screen="reset-password">
        <StatusBar style="light" />
        <Text style={styles.title}>Link expired or invalid</Text>
        <Text style={styles.hint}>
          Request a new reset link from the login page. Links are single-use and expire after a
          while.
        </Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.navigate('ForgotPassword')}
        >
          <Text style={styles.primaryButtonText}>Request new link</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.backLink} onPress={() => navigation.navigate('Login')}>
          <Text style={styles.backLinkText}>← Back to login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (done) {
    return (
      <View style={styles.centered} data-screen="reset-password">
        <StatusBar style="light" />
        <Text style={styles.title}>Password updated</Text>
        <Text style={styles.hint}>You can now sign in with your new password.</Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.navigate('Login')}
        >
          <Text style={styles.primaryButtonText}>Go to login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      data-screen="reset-password"
    >
      <StatusBar style="light" />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          <Text style={styles.title}>Choose a new password</Text>
          <Text style={styles.hint}>Enter and confirm your new password below.</Text>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>New password</Text>
            <View style={styles.passwordFieldOuter}>
              <TextInput
                style={styles.passwordInputInner}
                placeholder="At least 8 characters"
                placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!passwordVisible}
                autoCapitalize="none"
                autoCorrect={false}
                editable={!busy}
              />
              <TouchableOpacity
                onPress={() => setPasswordVisible((v) => !v)}
                style={styles.passwordToggle}
                accessibilityRole="button"
                accessibilityLabel={passwordVisible ? 'Hide password' : 'Show password'}
              >
                <MaterialIcons
                  name={passwordVisible ? 'visibility-off' : 'visibility'}
                  size={22}
                  color={LOGIN_GOLD}
                />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Confirm password</Text>
            <TextInput
              style={styles.input}
              placeholder="Repeat new password"
              placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!passwordVisible}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!busy}
            />
          </View>

          <TouchableOpacity
            style={[styles.primaryButton, busy && styles.primaryButtonDisabled]}
            onPress={() => void handleSubmit()}
            disabled={busy}
            activeOpacity={0.85}
          >
            <Text style={styles.primaryButtonText}>
              {busy ? 'Saving…' : 'Update password'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.backLink}
            onPress={() => navigation.navigate('Login')}
            disabled={busy}
          >
            <Text style={styles.backLinkText}>← Back to login</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const getStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#000000',
    },
    centered: {
      flex: 1,
      backgroundColor: '#000000',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 30,
    },
    scrollContent: {
      flexGrow: 1,
      justifyContent: 'center',
      paddingHorizontal: 30,
      paddingVertical: 40,
    },
    content: {
      width: '100%',
      maxWidth: 420,
      alignSelf: 'center',
    },
    title: {
      fontSize: 28,
      fontWeight: 'bold',
      color: LOGIN_GOLD,
      textAlign: 'center',
      marginBottom: 12,
    },
    hint: {
      fontSize: 15,
      lineHeight: 22,
      color: '#CCCCCC',
      textAlign: 'center',
      marginBottom: 28,
    },
    inputContainer: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      color: LOGIN_GOLD,
      marginBottom: 8,
      fontWeight: '600',
    },
    input: {
      backgroundColor: '#2a2a2a',
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 14,
      fontSize: 16,
      color: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#3a3a3a',
    },
    passwordFieldOuter: {
      position: 'relative',
      backgroundColor: '#2a2a2a',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#3a3a3a',
      minHeight: 50,
      justifyContent: 'center',
    },
    passwordInputInner: {
      width: '100%',
      paddingLeft: 16,
      paddingRight: 52,
      paddingVertical: 14,
      fontSize: 16,
      color: '#FFFFFF',
      backgroundColor: '#2a2a2a',
      borderWidth: 0,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    passwordToggle: {
      position: 'absolute',
      right: 4,
      top: 0,
      bottom: 0,
      width: 44,
      justifyContent: 'center',
      alignItems: 'center',
    },
    primaryButton: {
      backgroundColor: LOGIN_GOLD,
      borderRadius: 8,
      paddingVertical: 16,
      paddingHorizontal: 24,
      alignItems: 'center',
      marginTop: 4,
    },
    primaryButtonDisabled: {
      opacity: 0.85,
    },
    primaryButtonText: {
      color: '#000000',
      fontSize: 17,
      fontWeight: 'bold',
    },
    backLink: {
      marginTop: 24,
      alignItems: 'center',
    },
    backLinkText: {
      color: LOGIN_GOLD,
      fontSize: 14,
    },
  });
